import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, useTheme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';

const TIMEFRAME_OPTIONS = [
  { days: 30, key: '1m' },
  { days: 90, key: '3m' },
  { days: 180, key: '6m' },
  { days: 365, key: '1y' },
];

function BreadthTrendChart({ breadthData, selectedDays, onDaysChange }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!breadthData || breadthData.length === 0) return null;

  const hasStockData = breadthData.some(d => d.stock_pct_above_ma20 != null && d.total_stocks_ma20 > 0);
  const hasVnindexData = breadthData.some(d => d.vnindex_close != null);

  // API returns DESC (newest first); chart reads left → right (oldest → newest)
  const formatted = [...breadthData]
    .sort((a, b) => (a.trade_date || '').localeCompare(b.trade_date || ''))
    .map(d => ({
    ...d,
    date: d.trade_date?.slice(5) || '',
    ma20: Number((d.pct_above_ma20 ?? 0).toFixed(1)),
    ma50: Number((d.pct_above_ma50 ?? 0).toFixed(1)),
    stockMa20: d.stock_pct_above_ma20 != null ? Number(d.stock_pct_above_ma20.toFixed(1)) : null,
    stockMa50: d.stock_pct_above_ma50 != null ? Number(d.stock_pct_above_ma50.toFixed(1)) : null,
    vnindex: d.vnindex_close != null ? Number(d.vnindex_close.toFixed(2)) : null,
  }));


  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 2px 8px rgba(0,0,0,.08)',
      }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
        {payload.map((p, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography variant="caption">
              {p.name}: <b>{p.dataKey === 'vnindex' ? p.value : `${p.value}%`}</b>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
      bgcolor: 'background.paper', p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 2.5 },
    }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
        {t('marketBreadth.chart.title')}
      </Typography>

      {onDaysChange && (
        <ToggleButtonGroup
          value={selectedDays}
          exclusive
          size="small"
          onChange={(_e, value) => value != null && onDaysChange(value)}
          sx={{ mb: 1.5, '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: '0.72rem', textTransform: 'none' } }}
        >
          {TIMEFRAME_OPTIONS.map(opt => (
            <ToggleButton key={opt.days} value={opt.days}>
              {t(`marketBreadth.chart.timeframe.${opt.key}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={formatted} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
          <defs>
            <linearGradient id="ma20Fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="ma50Fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#f0f0f0'} />
          <XAxis
            dataKey="date" tick={{ fontSize: 11 }}
            stroke={isDark ? '#555' : '#ccc'} tickLine={false}
          />
          <YAxis
            yAxisId="left"
            domain={[0, 100]} tick={{ fontSize: 11 }}
            stroke={isDark ? '#555' : '#ccc'} tickLine={false}
            tickFormatter={v => `${v}%`}
          />
          {hasVnindexData && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={['auto', 'auto']} tick={{ fontSize: 11 }}
              stroke={isDark ? '#555' : '#ccc'} tickLine={false}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine yAxisId="left" y={50} stroke={isDark ? '#444' : '#e5e7eb'} strokeDasharray="4 4" />
          {hasStockData ? (
            <>
              <Area
                yAxisId="left"
                type="monotone" dataKey="stockMa20" name={t('marketBreadth.chart.stockMA20')}
                stroke="#6366f1" strokeWidth={2}
                fill="url(#ma20Fill)" dot={false} activeDot={{ r: 3 }}
              />
              <Area
                yAxisId="left"
                type="monotone" dataKey="stockMa50" name={t('marketBreadth.chart.stockMA50')}
                stroke="#10b981" strokeWidth={2}
                fill="url(#ma50Fill)" dot={false} activeDot={{ r: 3 }}
              />
              <Area
                yAxisId="left"
                type="monotone" dataKey="ma20" name={t('marketBreadth.chart.sectorMA20')}
                stroke="#6366f1" strokeWidth={1} strokeDasharray="4 4"
                fill="none" dot={false} activeDot={{ r: 2 }}
                opacity={0.4}
              />
              <Area
                yAxisId="left"
                type="monotone" dataKey="ma50" name={t('marketBreadth.chart.sectorMA50')}
                stroke="#10b981" strokeWidth={1} strokeDasharray="4 4"
                fill="none" dot={false} activeDot={{ r: 2 }}
                opacity={0.4}
              />
            </>
          ) : (
            <>
              <Area
                yAxisId="left"
                type="monotone" dataKey="ma20" name={t('marketBreadth.chart.ma20')}
                stroke="#6366f1" strokeWidth={2}
                fill="url(#ma20Fill)" dot={false} activeDot={{ r: 3 }}
              />
              <Area
                yAxisId="left"
                type="monotone" dataKey="ma50" name={t('marketBreadth.chart.ma50')}
                stroke="#10b981" strokeWidth={2}
                fill="url(#ma50Fill)" dot={false} activeDot={{ r: 3 }}
              />
            </>
          )}
          {hasVnindexData && (
            <Line
              yAxisId="right"
              type="monotone" dataKey="vnindex" name={t('marketBreadth.chart.vnindex')}
              stroke="#f59e0b" strokeWidth={2}
              dot={false} activeDot={{ r: 3 }}
            />
          )}
          <Legend
            iconType="circle" iconSize={8}
            wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

BreadthTrendChart.propTypes = {
  breadthData: PropTypes.arrayOf(PropTypes.shape({
    trade_date: PropTypes.string,
    pct_above_ma20: PropTypes.number,
    pct_above_ma50: PropTypes.number,
    stock_pct_above_ma20: PropTypes.number,
    stock_pct_above_ma50: PropTypes.number,
    total_stocks_ma20: PropTypes.number,
    total_stocks_ma50: PropTypes.number,
    vnindex_close: PropTypes.number,
    vnindex_change_pct: PropTypes.number,
  })),
  selectedDays: PropTypes.number,
  onDaysChange: PropTypes.func,
};

export default BreadthTrendChart;
